import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface BoletoComprador {
  folio: string;
  nombre: string;
  correo: string;
}

export interface CrearPaymentIntentRequest {
  folio: string;
  currency: string;
  customerEmail?: string;
  /** Un elemento por boleto con el comprador asignado. */
  boletosCompradores: BoletoComprador[];
}

export interface CrearPaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
  currency: string;
}

export interface CrearOxxoIntentRequest {
  folio: string;
  currency: string;
  customerEmail: string;
  customerName: string;
  boletosCompradores: BoletoComprador[];
}

export interface CrearOxxoIntentResponse {
  paymentIntentId: string;
  referencia: string;
  hostedVoucherUrl: string;
  expiresAt: string | null;
  amount: number;
  currency: string;
}

export interface CrearSpeiIntentRequest {
  folio: string;
  currency: string;
  customerEmail: string;
  customerName: string;
  boletosCompradores: BoletoComprador[];
}

export interface CrearSpeiIntentResponse {
  paymentIntentId: string;
  clabe: string;
  bankName: string;
  referencia: string;
  hostedInstructionsUrl: string;
  expiresAt: string | null;
  amount: number;
  currency: string;
}

export interface BoletoMetodoItem {
  folio: string;
  claveColaborador: number;
  claveSorteo: number;
}

export interface MetodosPagoRequest {
  /** 1=Todos, 2=Liga de Colaborador, 3=Público General */
  publico: number;
  boletos: BoletoMetodoItem[];
}

export interface MetodosPagoFlags {
  mit: boolean;
  referencia: boolean;
  paypal: boolean;
  stripe: boolean;
  oxxo: boolean;
  spei: boolean;
}

export interface MetodosPagoResponse {
  metodosPermitidos: number[];
  flags: MetodosPagoFlags;
  origen: string;
}

/** Límite máximo por transacción. Solo se mandan folios: el total lo calcula APIs Pagos con BOLETOS.COSTO. */
export interface ValidarMontoResponse {
  activo: boolean;
  permitido: boolean;
  total: number;
  montoMaximo: number;
  /** Métodos a los que aplica el límite (1 MIT, 2 Referencia, 3 PayPal, 6 Stripe, 7 OXXO, 8 SPEI). */
  metodos: number[];
}

@Injectable({ providedIn: 'root' })
export class StripePagoService {
  private baseUrl = environment.UrlPagosUnificados;

  constructor(private http: HttpClient) {}

  /**
   * Consulta al microservicio qué métodos de pago mostrar según la configuración parametrizada.
   * Se fuerza SIN caché (headers no-cache + cache-buster) para que un cambio de configuración
   * en SGSA se refleje de inmediato y no se quede una respuesta vieja cacheada.
   */
  getMetodosPago(request: MetodosPagoRequest): Observable<MetodosPagoResponse> {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    });
    const url = `${this.baseUrl}api/payments/metodos-pago?_=${Date.now()}`;
    return this.http.post<MetodosPagoResponse>(url, request, { headers });
  }

  /**
   * Valida el límite máximo por transacción del carrito. Si la API falla, se resuelve como
   * permitido (no se frena la venta), igual que getMetodosPago cae a los métodos default.
   */
  validarMonto(folios: string[]): Promise<ValidarMontoResponse> {
    const permitido: ValidarMontoResponse = { activo: false, permitido: true, total: 0, montoMaximo: 0, metodos: [] };
    const url = `${this.baseUrl}api/payments/validar-monto?_=${Date.now()}`;
    return this.http
      .post<ValidarMontoResponse>(url, { folios })
      .toPromise()
      .then((res) => res || permitido)
      .catch(() => permitido);
  }

  crearPaymentIntent(request: CrearPaymentIntentRequest): Observable<CrearPaymentIntentResponse> {
    return this.http.post<CrearPaymentIntentResponse>(`${this.baseUrl}api/payments/create-intent`, request);
  }

  crearOxxoIntent(request: CrearOxxoIntentRequest): Observable<CrearOxxoIntentResponse> {
    return this.http.post<CrearOxxoIntentResponse>(`${this.baseUrl}api/payments/create-oxxo-intent`, request);
  }

  crearSpeiIntent(request: CrearSpeiIntentRequest): Observable<CrearSpeiIntentResponse> {
    return this.http.post<CrearSpeiIntentResponse>(`${this.baseUrl}api/payments/create-spei-intent`, request);
  }

  /**
   * Cancela el intento de pago ANTERIOR de un boleto para poder volver a pagarlo.
   * Es lo que permite reintentar con Stripe las veces que se quiera, en vez de esperar
   * 24 horas como con MIT.
   *
   * Se llama al agregar el boleto al carrito, ANTES de validar.
   * Si responde puedeContinuar = false, el flujo DEBE detenerse y mostrar el mensaje:
   * significa que el pago anterior si se cobro (o que no se pudo verificar).
   */
  cancelarIntentPrevio(folioBoleto: string): Observable<CancelarIntentPrevioResponse> {
    return this.http.post<CancelarIntentPrevioResponse>(
      `${this.baseUrl}api/payments/cancelar-intent-previo`, { folioBoleto });
  }

  consultarEstadoOrden(folio: string): Observable<{ estado: string }> {
    return this.http.get<{ estado: string }>(`${this.baseUrl}api/payments/estado/${folio}`);
  }
}

/**
 * motivo: SIN_ORDEN_PREVIA | CANCELADO | NO_ERA_STRIPE  -> puedeContinuar = true
 *         YA_PAGADO | EN_PROCESO | ERROR_STRIPE         -> puedeContinuar = false
 */
export interface CancelarIntentPrevioResponse {
  puedeContinuar: boolean;
  motivo: string;
  mensaje: string;
  folioPrevio?: string;
}
